// Pure helpers behind TanTable's Excel-style Year > Month > Day date filter.
// No React here so every rule is unit-testable in isolation.
//
// A date column's facet values are canonical day keys ("YYYY-MM-DD") plus the
// shared "(Blank)" sentinel (and, for values that refuse to parse, their raw
// text so nothing is silently hidden). The day key is taken TEXTUALLY from ISO
// strings — never through `new Date()` — so a trailing "Z" or offset can't
// shift a day across the local timezone.
//
// Design: docs/plans/2026-09-02-tantable-date-tree-filter-design.md

export const BLANK_FACET = '(Blank)';

const ZERO_DATE_RE = /^0000-00-00/;
const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ]|$)/;
const SLASH_RE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s|$)/;
const SLASH_YY_RE = /^(\d{1,2})\/(\d{1,2})\/(\d{2})(?:\s|$)/;
const MONTH_DAY_RE = /^(\d{1,2})\/(\d{1,2})$/;

const pad2 = (n: number | string) => String(n).padStart(2, '0');

const buildKey = (yyyy: number, mm: number, dd: number): string | null => {
  if (yyyy < 1 || mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
  return `${String(yyyy).padStart(4, '0')}-${pad2(mm)}-${pad2(dd)}`;
};

export const isBlankDate = (value: unknown): boolean =>
  value == null || String(value) === '' || ZERO_DATE_RE.test(String(value));

/**
 * Parse a cell or saved-filter value to a "YYYY-MM-DD" day key, or null when it
 * isn't a date. `allowMonthDay` additionally accepts the legacy bare "MM/DD"
 * form TanTable used to persist (current year implied) — only for normalizing
 * saved selections, never for detecting date columns.
 */
export const parseDayKey = (
  value: unknown,
  opts: { allowMonthDay?: boolean } = {},
): string | null => {
  if (value == null) return null;
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    return buildKey(value.getFullYear(), value.getMonth() + 1, value.getDate());
  }
  const s = String(value).trim();
  if (s === '' || ZERO_DATE_RE.test(s)) return null;

  let m = s.match(ISO_RE);
  if (m) return buildKey(+m[1], +m[2], +m[3]);
  m = s.match(SLASH_RE);
  if (m) return buildKey(+m[3], +m[1], +m[2]);
  m = s.match(SLASH_YY_RE);
  if (m) return buildKey(2000 + +m[3], +m[1], +m[2]);
  if (opts.allowMonthDay) {
    m = s.match(MONTH_DAY_RE);
    if (m) return buildKey(new Date().getFullYear(), +m[1], +m[2]);
  }
  return null;
};

/** Facet value for a date-column cell: day key, (Blank), or raw text. */
export const toDateFacetValue = (value: unknown): string => {
  if (isBlankDate(value)) return BLANK_FACET;
  return parseDayKey(value) ?? String(value);
};

// ---------------------------------------------------------------------------
// Detection
// ---------------------------------------------------------------------------

/** Minimal structural view of a TanStack ColumnDef — enough to find leaves. */
export interface DateColumnDefLike {
  id?: string;
  accessorKey?: string | number;
  accessorFn?: (row: any, index: number) => unknown;
  columns?: DateColumnDefLike[];
  meta?: { dateFilter?: boolean } & Record<string, unknown>;
}

interface LeafAccessor {
  id: string;
  meta?: DateColumnDefLike['meta'];
  get: (row: any, index: number) => unknown;
}

const readPath = (row: any, path: string): unknown =>
  path.split('.').reduce<any>((acc, k) => (acc == null ? undefined : acc[k]), row);

// TanStack derives a column id from accessorKey with "." -> "_" when no id is given.
const collectLeafAccessors = (
  columns: DateColumnDefLike[],
  out: LeafAccessor[] = [],
): LeafAccessor[] => {
  for (const col of columns) {
    if (col.columns && col.columns.length > 0) {
      collectLeafAccessors(col.columns, out);
      continue;
    }
    const key = col.accessorKey != null ? String(col.accessorKey) : undefined;
    const id = col.id ?? (key ? key.replace('.', '_') : undefined);
    if (!id) continue;
    const accessorFn = col.accessorFn;
    const get: LeafAccessor['get'] = accessorFn
      ? (row, i) => accessorFn(row, i)
      : key
        ? (row) => readPath(row, key)
        : () => undefined;
    out.push({ id, meta: col.meta, get });
  }
  return out;
};

/**
 * Per-column cell readers keyed by column id, honoring accessorFn / accessorKey
 * (dotted paths included). Use this wherever a raw row must be read "the way the
 * column reads it" outside TanStack — e.g. the tab-count matcher — since a column
 * id (`live`, `rs`) is often not a row key (`isLiveLoad`, `rescheduleCount`).
 */
export const buildColumnValueReaders = (
  columns: DateColumnDefLike[],
): Map<string, (row: any, index: number) => unknown> => {
  const readers = new Map<string, (row: any, index: number) => unknown>();
  for (const leaf of collectLeafAccessors(columns)) readers.set(leaf.id, leaf.get);
  return readers;
};

/** True when at least one value is non-blank and every non-blank value is a date. */
export const isDateLikeColumn = (values: Iterable<unknown>): boolean => {
  let sawDate = false;
  for (const v of values) {
    if (isBlankDate(v)) continue;
    if (parseDayKey(v) == null) return false;
    sawDate = true;
  }
  return sawDate;
};

/**
 * Effective date columns = explicit prop ∪ auto-detected, with `meta.dateFilter`
 * forcing a column on (true) or off (false). Bails out of a column at the first
 * non-date value, so the scan is cheap even on large grids.
 */
export const resolveDateColumns = (
  rows: any[],
  columns: DateColumnDefLike[],
  explicit: string[],
): Set<string> => {
  const result = new Set<string>(explicit);
  const leaves = collectLeafAccessors(columns);
  for (const leaf of leaves) {
    const override = leaf.meta?.dateFilter;
    if (override === false) {
      result.delete(leaf.id);
      continue;
    }
    if (override === true || result.has(leaf.id)) {
      result.add(leaf.id);
      continue;
    }
    const values = (function* () {
      for (let i = 0; i < rows.length; i++) yield leaf.get(rows[i], i);
    })();
    if (isDateLikeColumn(values)) result.add(leaf.id);
  }
  return result;
};

// ---------------------------------------------------------------------------
// Tree
// ---------------------------------------------------------------------------

export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export interface DateFacet {
  value: string;
  count: number;
}

export interface DateTreeNode {
  level: 'year' | 'month' | 'day' | 'other';
  /** Group key for year ("2026") / month ("2026-02"); selection key for day/other. */
  key: string;
  label: string;
  /** What the popover search box matches against. */
  searchLabel: string;
  count: number;
  children: DateTreeNode[];
}

const DAY_KEY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** (Blank) and any raw leaves first, then the Year > Month > Day tree (ascending). */
export const buildDateTree = (facets: DateFacet[]): DateTreeNode[] => {
  const years = new Map<string, Map<string, DateTreeNode[]>>();
  const others: DateTreeNode[] = [];
  for (const f of facets) {
    const m = f.value.match(DAY_KEY_RE);
    if (!m) {
      others.push({
        level: 'other', key: f.value, label: f.value, searchLabel: f.value, count: f.count, children: [],
      });
      continue;
    }
    const [, yyyy, mm, dd] = m;
    const months = years.get(yyyy) ?? new Map<string, DateTreeNode[]>();
    years.set(yyyy, months);
    const days = months.get(mm) ?? [];
    months.set(mm, days);
    days.push({
      level: 'day', key: f.value, label: dd, searchLabel: `${mm}/${dd}/${yyyy}`, count: f.count, children: [],
    });
  }
  const byKey = (a: DateTreeNode, b: DateTreeNode) => a.key.localeCompare(b.key);
  const tree: DateTreeNode[] = [...years.keys()].sort().map((yyyy) => {
    const months = years.get(yyyy)!;
    const monthNodes: DateTreeNode[] = [...months.keys()].sort().map((mm) => {
      const days = months.get(mm)!.sort(byKey);
      const name = MONTH_NAMES[+mm - 1] ?? mm;
      return {
        level: 'month',
        key: `${yyyy}-${mm}`,
        label: name,
        searchLabel: name,
        count: days.reduce((s, d) => s + d.count, 0),
        children: days,
      };
    });
    return {
      level: 'year',
      key: yyyy,
      label: yyyy,
      searchLabel: yyyy,
      count: monthNodes.reduce((s, m) => s + m.count, 0),
      children: monthNodes,
    };
  });
  // Non-date leaves sit ABOVE the years so "(Blank)" is always one glance away
  // (John, 2026-09-02); (Blank) first, then raw text alphabetically.
  others.sort((a, b) =>
    a.key === BLANK_FACET ? -1
      : b.key === BLANK_FACET ? 1
        : a.key.localeCompare(b.key, undefined, { numeric: true }));
  return [...others, ...tree];
};

/** Every selectable (day / other) key under a node, in tree order. */
export const leafKeys = (node: DateTreeNode): string[] =>
  node.children.length === 0 ? [node.key] : node.children.flatMap(leafKeys);

export type NodeCheckState = 'checked' | 'unchecked' | 'indeterminate';

export const nodeCheckState = (
  node: DateTreeNode,
  isSelected: (key: string) => boolean,
): NodeCheckState => {
  const keys = leafKeys(node);
  let hit = 0;
  for (const k of keys) if (isSelected(k)) hit++;
  if (hit === 0) return 'unchecked';
  return hit === keys.length ? 'checked' : 'indeterminate';
};

/**
 * Prune the tree to nodes matching `text` (case-insensitive substring of the
 * node's searchLabel). A matching node keeps all its descendants; a
 * non-matching group survives only if some descendant matches. Group counts
 * are recomputed over what survived. Empty text returns the input untouched.
 */
export const filterTreeBySearch = (nodes: DateTreeNode[], text: string): DateTreeNode[] => {
  const q = text.trim().toLowerCase();
  if (!q) return nodes;
  const prune = (node: DateTreeNode): DateTreeNode | null => {
    if (node.searchLabel.toLowerCase().includes(q)) return node;
    if (node.children.length === 0) return null;
    const kept = node.children.map(prune).filter((n): n is DateTreeNode => n !== null);
    if (kept.length === 0) return null;
    return { ...node, children: kept, count: kept.reduce((s, c) => s + c.count, 0) };
  };
  return nodes.map(prune).filter((n): n is DateTreeNode => n !== null);
};

// ---------------------------------------------------------------------------
// Legacy saved selections
// ---------------------------------------------------------------------------

// Saved selections from before the tree used "MM/DD" (current year implied) or
// "MM/DD/YYYY" display strings, and undeclared date columns stored raw ISO
// datetimes. Normalize to day keys lazily wherever a selection is read; cache by
// array identity so the hot filter path pays once per stored value.
const normalizeCache = new WeakMap<string[], string[]>();

export const normalizeDateSelection = (values: string[]): string[] => {
  const hit = normalizeCache.get(values);
  if (hit) return hit;
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    const key = v === BLANK_FACET ? v : (parseDayKey(v, { allowMonthDay: true }) ?? v);
    if (!seen.has(key)) {
      seen.add(key);
      out.push(key);
    }
  }
  normalizeCache.set(values, out);
  return out;
};
